import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { getIdMeBaseApiUrl } from '../../utils/UrlConstants';
import { toast } from 'react-toastify';
import { useMsal } from '@azure/msal-react';
import { checkUserAuthentication, handleUnauthenticatedUser } from "../../utils/authUtils";
import { useIdentityAppService } from '../../services/IdentityServices';

interface RedirectResponse {
    success: boolean;
    redirectUrl: string;
    message?: string;
    timestamp: string;
}

const ApiRedirect: React.FC = () => {
    const navigate = useNavigate();
    const { accounts } = useMsal();
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        if (!checkUserAuthentication()) {
            handleUnauthenticatedUser();
            return;
        }
        
        const fetchRedirectUrl = async () => {
            try {
                setLoading(true);
                setError(null);

                // Make API call to get redirect URL
                const response = await useIdentityAppService().getSendGridRedirect();

                if (response.ok) {
                    const data: RedirectResponse = await response.json();

                    if (data.success && data.redirectUrl) {
                        console.log('Redirecting to:', data.redirectUrl);

                        // Check if it's an external URL
                        if (data.redirectUrl.startsWith('http://') || data.redirectUrl.startsWith('https://')) {
                            // External URL - use window.location
                            window.location.href = data.redirectUrl;
                        } else {
                            // Internal route - use React Router navigate
                            navigate(data.redirectUrl);
                        }
                    } else {
                        throw new Error(data.message || 'Invalid redirect URL received');
                    }
                } else {
                    const errorData = await response.json().catch(() => ({
                        error: `HTTP ${response.status}: ${response.statusText}`
                    }));
                    throw new Error(errorData.error || errorData.message || 'Failed to fetch redirect URL');
                }

            } catch (error) {
                console.error('Error fetching redirect URL:', error);
                const errorMessage = error instanceof Error ? error.message : 'An unknown error occurred';
                setError(errorMessage);
                toast.error(`Redirect failed: ${errorMessage}`);

                // Optional: Navigate to a fallback route after error
                setTimeout(() => {
                    navigate('/admin-dashboard'); // or wherever you want to fallback
                }, 3000);
            } finally {
                setLoading(false);
            }
        };

        fetchRedirectUrl();
    }, [navigate, accounts]);

    // Optional: Show loading or error state
    if (loading) {
        return (
            <div style={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                height: '100vh',
                fontSize: '18px'
            }}>
                Redirecting...
            </div>
        );
    }

    if (error) {
        return (
            <div style={{
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                alignItems: 'center',
                height: '100vh',
                fontSize: '16px',
                color: '#721c24'
            }}>
                <p>Redirect failed: {error}</p>
                <p style={{ fontSize: '14px', color: '#666' }}>
                    You will be redirected to the dashboard shortly...
                </p>
            </div>
        );
    }

    // This should rarely render since we redirect immediately
    return null;
};

export default ApiRedirect;